import type React from 'react';
import { assetsPath } from './constants';

export interface CustomCommonLink {
  id: string;
  type: string;
}

export interface CustomTextLink extends CustomCommonLink {
  type: 'link';
  textId: keyof typeof import('@/i18n/translations/en.json');
  anchorProps: {
    en: RequiredPick<React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'href'>;
    fr: RequiredPick<React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'href'>;
  };
}

export interface CustomImageLink extends CustomCommonLink {
  type: 'image';
  ariaLabel: string;
  image: string;
  imageHover: string;
  imageAlt: string;
  anchorProps: RequiredPick<
    React.DetailedHTMLProps<React.AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>,
    'href'
  >;
  width: number;
  height: number;
}

export interface ServiceData {
  image: string;
  width: number;
  height: number;
  title: keyof typeof import('@/i18n/translations/en.json');
  /** If alt is not supplied, its value will resolve to `"${title}Alt"` */
  altText?: keyof typeof import('@/i18n/translations/en.json');
  /** If description is not supplied, its value will resolve to `"${title}Description"` */
  description?: keyof typeof import('@/i18n/translations/en.json');
}

export const servicesData: ServiceData[] = [
  {
    image: `${assetsPath}/site-media/free-shipping.png`,
    title: 'freeShipping',
    width: 56,
    height: 36,
  },
  {
    image: `${assetsPath}/site-media/free-returns.png`,
    title: 'freeReturns',
    width: 39,
    height: 52,
  },
  {
    image: `${assetsPath}/site-media/3-free-samples.png`,
    title: 'freeSamples',
    width: 56,
    height: 46,
  },

  {
    image: `${assetsPath}/site-media/pco-card.png`,
    altText: 'pcoCardAlt',
    title: 'earnRedeem',
    description: 'pcoCardDescription',
    width: 56,
    height: 38,
  },
];

export const locationLinks: CustomTextLink[] = [
  {
    id: 'sitemap-link',
    textId: 'sitemap',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://www1.shoppersdrugmart.ca/en/Sitemap2',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://www1.shoppersdrugmart.ca/fr/Sitemap2',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
  {
    id: 'language-toggle',
    textId: 'language',
    type: 'link',
    anchorProps: {
      en: {
        href: '?lang=fr',
      },
      fr: {
        href: '?lang=en',
      },
    },
  },
  {
    id: 'accessibility-link',
    textId: 'accessibility',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://www1.shoppersdrugmart.ca/en/Contact-Us/Accessibility-Standard',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://www1.shoppersdrugmart.ca/fr/Contact-Us/Accessibility-Standard',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
  {
    id: 'locator-link',
    textId: 'storelocator',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://stores.shoppersdrugmart.ca/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://stores.shoppersdrugmart.ca/fr/',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
];

export const socialMediaLinks: CustomImageLink[] = [
  {
    id: 'twitter-link',
    type: 'image',
    anchorProps: {
      href: 'https://twitter.com/ShopprsDrugMart',
    },
    ariaLabel: 'Twitter',
    image: `${assetsPath}/site-icons/2020-site-icons/twitter.svg`,
    imageHover: `${assetsPath}/site-icons/2020-site-icons/twitter-grey.svg`,
    imageAlt: 'Twitter logo',
    width: 28,
    height: 28,
  },
  {
    id: 'facebook-link',
    type: 'image',
    anchorProps: {
      href: 'https://www.facebook.com/shoppersdrugmart',
    },
    ariaLabel: 'Facebook',
    image: `${assetsPath}/site-icons/2020-site-icons/facebook.svg`,
    imageHover: `${assetsPath}/site-icons/2020-site-icons/facebook-grey.svg`,
    imageAlt: 'Facebook logo',
    width: 28,
    height: 28,
  },
  {
    id: 'pinterest-link',
    type: 'image',
    anchorProps: {
      href: 'https://www.pinterest.ca/shopprsdrugmart/',
    },
    ariaLabel: 'Pinterest',
    image: `${assetsPath}/site-icons/2020-site-icons/pinterest.svg`,
    imageHover: `${assetsPath}/site-icons/2020-site-icons/pinterest-grey.svg`,
    imageAlt: 'Pinterest logo',
    width: 28,
    height: 28,
  },
  {
    id: 'instagram-link',
    type: 'image',
    anchorProps: {
      href: 'https://www.instagram.com/shoppersdrugmart/',
    },
    ariaLabel: 'Instagram',
    image: `${assetsPath}/site-icons/2020-site-icons/instagram.svg`,
    imageHover: `${assetsPath}/site-icons/2020-site-icons/instagram-grey.svg`,
    imageAlt: 'Instagram logo',
    width: 28,
    height: 28,
  },
];

export const condensedFooterLinks: CustomTextLink[] = [
  {
    id: 'returns-exchange-link',
    textId: 'returnExchange',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://shop.shoppersdrugmart.ca/returns?lang=en',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://shop.shoppersdrugmart.ca/returns?lang=fr',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
  {
    id: 'shipping-handling-link',
    textId: 'shippingHandling',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://shop.shoppersdrugmart.ca/shipping?lang=en',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://shop.shoppersdrugmart.ca/shipping?lang=fr',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
  {
    id: 'contact-us-link',
    textId: 'contactUs',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://www1.shoppersdrugmart.ca/en/contact-us',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://www1.shoppersdrugmart.ca/fr/contact-us',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
  {
    id: 'faq-link',
    textId: 'faq',
    type: 'link',
    anchorProps: {
      en: {
        href: 'https://www1.shoppersdrugmart.ca/en/faq',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      fr: {
        href: 'https://www1.shoppersdrugmart.ca/fr/faq',
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    },
  },
];
